import { usePropertyBuilder } from "@/components/views/config/helpers";
import { useSteamWorkshop } from "@/components/views/mods/steam_workshop";
import type { ProjectZomboidProfileNamespace, SteamWorkshopItem, ZomboidMods } from "@/graphql";
import DialogConfirm from "@/components/dialogs/DialogConfirm.vue";
import { useDialogStore } from "@/store/dialog";
import { useServiceStore } from "@/store/service";
import {
  faClockRotateLeft,
  faFileImport,
  faLink,
  faLinkHorizontal,
  faPuzzlePiece,
  faSliders,
  faTriangleExclamation,
} from "@fortawesome/pro-solid-svg-icons";
import { computed, markRaw } from "vue";
import { i18n } from "@/i18n";
import { commonMenuItems } from "@/game_profiles/common";
import defaultProfile, { asyncRoute } from "@/game_profiles/default";
import type { IUseMods, ModRelation, ModTag, RelatedMod } from "@/components/views/mods";
import { type ConfigSection, type GameProfile, type Menu, MenuItem, type ProfileCtx } from "./types";

const STEAM_ITEM_URL = "https://steamcommunity.com/sharedfiles/filedetails/?id=";

const extendedSettingsMenuItem = new MenuItem(
  "extended-settings",
  faSliders,
  asyncRoute(() => import("../components/views/config/project_zomboid/PZExtendedConf.vue")),
);

const createMenu = (): Menu => {
  const menu = defaultProfile().menu;
  // menu[0].items = [status, settings, fileManager] — insert Extended Settings between settings and fileManager
  menu[0].items.splice(2, 0, extendedSettingsMenuItem);
  // Save Backups + Save Import (TR-5255) sit directly below File Manager, matching SCUM and
  // Palworld, above Logs. Labels come from the existing generic ngp/route/* keys.
  menu[0].items.push(
    new MenuItem(
      "save-backups",
      faClockRotateLeft,
      asyncRoute(() => import("../components/views/project_zomboid/SaveBackups.vue")),
    ),
    new MenuItem(
      "save-import",
      faFileImport,
      asyncRoute(() => import("../components/views/project_zomboid/SaveImport.vue")),
    ),
  );
  menu[0].items.push(commonMenuItems.logs);
  menu[0].items.push(
    new MenuItem(
      "mods",
      faPuzzlePiece,
      asyncRoute(() => import("../components/views/config/project_zomboid/Mods.vue")),
      [
        {
          route: "manage",
          component: asyncRoute(() => import("../components/views/mods/manage/ModsManage.vue")),
        },
        {
          route: "settings",
          component: asyncRoute(() => import("../components/views/config/project_zomboid/ModSettings.vue")),
        },
      ],
    ),
  );
  return menu;
};

// Main Settings page: "I just bought a server, get me online with my friends".
// Everything else lives on the Extended Settings page — the two lists must be kept in sync,
// see components/views/config/project_zomboid/PZExtendedConf.vue.
// This list is explicit (rather than buildConfigSections(ctx)) so newly added SandboxVars
// sections do not silently appear on the main page.
const configSections: ConfigSection[] = [
  { component: "ConfSection", props: { name: "branch" } },
  { component: "ConfSection", props: { name: "basic" } },
  { component: "ConfSection", props: { name: "mods" } },
  // PZGameplaySection keeps the SpawnKits <-> SpawnItems watchers; it must stay on this page.
  { component: "PZGameplaySection", props: { name: "gameplay" } },
  { component: "ConfSection", props: { name: "zombies" } },
  { component: "ConfSection", props: { name: "voice_chat" } },
];

/**
 * The mod model the backend computes on every config read (TR-5257 Phase A).
 *
 * Everything the tab renders is already in here — what is on disk, which `mod.info` each mod
 * resolved to for this server's build, and the validation verdicts. That matters because `Mods` and
 * `WorkshopItems` are **not** hot-reloadable: every wrong attempt costs the customer a full restart,
 * so the panel's job is to be right the first time rather than to be a fast live editor.
 */
export function useZomboidMods() {
  const service = useServiceStore();
  const profile = computed(() => service.ctx.profile as ProjectZomboidProfileNamespace | undefined);
  // Aliased in the query: a plain `mods` collides with Minecraft's `[MinecraftMod]` in the same
  // selection set on the profile union.
  return computed(() => (profile.value as unknown as { zomboidMods?: ZomboidMods } | undefined)?.zomboidMods);
}

/**
 * Selection surface for the shared mod components.
 *
 * `WorkshopItems=` **is** the selection (design decision D3): `Mods=`, `Map=` and
 * `servertest_spawnregions.lua` are all derived from it backend-side. So this hands
 * `useSteamWorkshop` the one list the customer actually owns, with reordering enabled — in Project
 * Zomboid the order *is* the load order.
 */
export function useMods() {
  const { buildProperty } = usePropertyBuilder();
  const service = useServiceStore();

  const profile = computed(() => service.ctx.profile as ProjectZomboidProfileNamespace | undefined);
  const selectedWorkshopItems = computed(() => profile.value?.selectedWorkshopItems ?? []);
  const workshopItems = buildProperty<Array<string>>("virtual", "WorkshopItems", []);
  // The dedicated server has never supported a collection id natively — a long-standing Indie Stone
  // forum request — so there is no second list to keep. A pasted collection is expanded into its
  // individual items at the moment it is added.
  const collections = computed({ get: () => [] as string[], set: () => {} });

  // `Mods=` as it currently stands. Read-only in practice — the derivation owns it — with one
  // exception, `removeModId` below.
  const activeModIds = buildProperty<Array<string>>("virtual", "Mods", []);

  const base = useSteamWorkshop(selectedWorkshopItems, workshopItems, collections, "", true);
  const mods = useZomboidMods();
  // ⚠️ The global instance, not `useI18n()`: `ModTags` calls `useMods()` from inside a computed
  // during render rather than from setup, where the composition API helper has no instance to bind
  // to. Same reason the ACC and DayZ profiles do it this way.
  const { t } = i18n.global;

  /**
   * Per-mod state, rendered as tags on the standard mod card.
   *
   * This is where Project Zomboid differs from every other game with a mod tab: adding a mod does
   * not install anything. The game downloads its own Workshop items at boot, so a freshly added mod
   * has no `mod.info` on disk and therefore no mod id for `Mods=` until the server has started once.
   * Saying so on the card is the difference between "nothing happened" and "this is expected".
   */
  function modTags(mod: SteamWorkshopItem) {
    const workshopId = mod.id?.toString();
    const item = (mods.value?.selected ?? []).find((entry) => entry.workshopId === workshopId);
    const tags: ModTag[] = [];

    // B.8f — the dependency relation, in whichever direction the card can answer.
    //
    // ⚠️ Only ever additive. A mod with no tag here is one we know nothing about, not one that is
    // safe to remove: pending items have only the catalog's `require=` data, which is thin on the
    // long tail, and every mod selected before B.8 shipped has no recorded origin at all.
    const dependents = dependentsOf(workshopId);
    if (dependents.length) {
      tags.push({
        // vue-i18n injects `count` into the plural message itself; the same shape as the
        // file manager's delete dialog.
        label: t("ngp/zomboid_mods/selected/required_by", dependents.length),
        icon: faLink,
        tooltip: dependents.map((entry) => entry.name).join(", "),
      });
    }
    if (item?.addedAs === "dependency") {
      const addedFor = item.addedFor.map((id) => selectedName(id));
      tags.push({
        label: t("ngp/zomboid_mods/selected/added_as_dependency"),
        icon: faLinkHorizontal,
        tooltip: addedFor.length ? t("ngp/zomboid_mods/selected/added_for", { mods: addedFor.join(", ") }) : undefined,
      });
    }
    // Search results are not selected, so they have no dependents — but they do declare what they
    // need, and knowing that *before* adding beats finding out after the next restart.
    const catalogRequires = !item ? externalRequires(mod.zomboid?.requires, mod.zomboid?.modIds, workshopId) : [];
    if (catalogRequires.length) {
      const named = catalogRequires.map((reference) => dependencyIndex.value.nameFor(reference));
      tags.push({
        label: t("ngp/zomboid_mods/selected/requires", named.length),
        icon: faLink,
        tooltip: named.join(", "),
      });
    }

    // ⚠️ Install state only for mods that are actually selected, and only when a scan ran. With no
    // scan every mod comes back `pending`, and labelling a mod that has been installed for months
    // "downloads on next start" is worse than saying nothing. Search results are not selected at
    // all, so they never carry an install state — but they still get the catalog tags below, which
    // is what browsing needs.
    if (item && mods.value?.scanned) {
      tags.push(item.pending ? t("ngp/zomboid_mods/selected/pending") : t("ngp/zomboid_mods/selected/downloaded"));
    }

    // ⚠️ The map badge comes only from the files, never from the catalog category. The category is
    // picked from the author's Steam tags, and a kitchen-sink mod that tags `Map` among a dozen
    // others comes out as a map mod — Authentic Z (2335368829) is categorised `Map` while being a
    // clothing pack. Claiming "this adds a map" drives real decisions (the map chain, spawn
    // regions), so it is asserted only where we read `media/maps` ourselves.
    if (item && item.mapFolders.length) {
      tags.push(t("ngp/zomboid_mods/selected/map"));
    }

    // The category itself is still worth showing while browsing, as the catalog's own label rather
    // than a claim of ours. `item.category` is filled from the same catalog on the config read, so
    // the selected and search paths agree by construction.
    const category = item?.category ?? mod.zomboid?.category;
    if (category) {
      tags.push(category);
    }
    return tags;
  }

  /**
   * The dependency relation in prose, for the manage list (B.8g).
   *
   * The same facts the card tags carry, but with room to name the mods — which is the whole answer
   * to *why do I have this*, and this is the page where mods get deleted.
   */
  function modNotes(mod: SteamWorkshopItem) {
    const workshopId = mod.id?.toString();
    const notes: string[] = [];

    const dependents = dependentsOf(workshopId);
    if (dependents.length) {
      notes.push(t("ngp/zomboid_mods/selected/required_by_named", { mods: joinNames(dependents) }));
    }
    const requirements = requirementsOf(workshopId);
    if (requirements.length) {
      notes.push(t("ngp/zomboid_mods/selected/requires_named", { mods: joinNames(requirements) }));
    }

    // Provenance only when it adds something the live graph did not: "added as a dependency" next to
    // "required by X" is the same sentence twice. What it uniquely answers is the case where the mod
    // it was added for is gone — which is exactly when the customer wonders why this is here.
    const origin = originOf(workshopId);
    if (origin?.addedAs === "dependency" && !dependents.length) {
      notes.push(
        origin.addedFor.length
          ? t("ngp/zomboid_mods/selected/added_for", { mods: origin.addedFor.join(", ") })
          : t("ngp/zomboid_mods/selected/added_as_dependency"),
      );
    }
    return notes;
  }

  /** Shown in the manage dialog — the same "not installed yet, that is normal" message, in prose. */
  function modHint(mod: SteamWorkshopItem) {
    if (!mods.value?.scanned) {
      return undefined;
    }
    const item = (mods.value?.selected ?? []).find((entry) => entry.workshopId === mod.id?.toString());
    return item?.pending ? t("ngp/zomboid_mods/selected/pending_hint") : undefined;
  }

  /**
   * Ask before removing a mod that another one requires.
   *
   * The panel already reports the breakage — but only *after* the fact, which means finding out you
   * broke something by reading an error next to a list you have already changed. Project Zomboid
   * mods declare their dependencies in `mod.info`, so the answer is known before the click.
   */
  async function removeMod(mod: SteamWorkshopItem) {
    const dependents = dependentsOf(mod.id?.toString());
    if (dependents.length) {
      const confirmed = await useDialogStore().createDialog({
        component: markRaw(DialogConfirm),
        props: {
          headerIcon: faTriangleExclamation,
          headerText: t("ngp/zomboid_mods/remove/confirm_title"),
          contentText: t("ngp/zomboid_mods/remove/confirm_text", {
            mod: mod.name,
            dependents: joinNames(dependents),
          }),
        },
      });
      if (!confirmed) {
        return;
      }
    }
    base.removeMod(mod);
  }

  /**
   * Selected Workshop items the backend model does not know about yet.
   *
   * `profile.mods` is computed on a config *read*, so anything added since is missing from it —
   * which is why the panel used to have nothing to say about a mod until it had been saved.
   */
  const justAdded = computed(() => {
    const known = new Set((mods.value?.selected ?? []).map((item) => item.workshopId));
    return base.installedMods.value.filter((mod) => !known.has(mod.id?.toString() ?? ""));
  });

  /**
   * The arguments `projectZomboidPendingIssues` needs to describe the unsaved selection (B.11).
   *
   * ⚠️ Data only — no watcher, no request. `useMods()` is called from inside a computed during
   * render (see `ModTags`), so anything that registers an effect here attaches to whichever
   * component happens to be rendering. Putting the fetch in this composable silently killed the save
   * bar's own change watcher, which is how you find out. `ModIssues` owns the request instead.
   */
  const pendingIssuesInput = computed(() => {
    // 🚨 The **live** selection, and only mods it will actually load. A mod's files sitting on disk
    // prove nothing: Steam leaves a removed item's folder in place for a while, and the game loads
    // exactly what `WorkshopItems=` lists. Sending every installed mod as "provided" made a genuinely
    // missing dependency look satisfied, so no notice appeared — until the save, when the backend's
    // own check (which filters by the selection, see `_effective_mod_ids`) reported it after all.
    // That is the "only shows up after saving" report, and it is the same trap as the leftover-files
    // bug: on-disk is not the same question as selected.
    const selected = new Set(base.subscribedFileIds.value);
    const provided = (mods.value?.installed ?? [])
      // A mod hand-uploaded to `mods/` has no Workshop id and is never deselected, so it always counts.
      .filter((mod) => !mod.workshopId || selected.has(mod.workshopId))
      .map((mod) => mod.modId);

    return {
      workshopIds: justAdded.value.map((mod) => mod.id?.toString() ?? "").filter(Boolean),
      providedModIds: [
        ...(mods.value?.selected ?? []).filter((item) => selected.has(item.workshopId)).flatMap((item) => item.modIds),
        ...provided,
      ],
      buildMajor: mods.value?.buildMajor,
    };
  });

  /**
   * The `require=` graph of the current selection, both directions, built once (B.8e).
   *
   * `dependentsOf` used to walk every mod for every call, and the cards call it per render — O(n²·m)
   * on a list that routinely runs to 40 items. Everything the tab needs is derivable from one pass.
   */
  const dependencyIndex = computed(() => {
    // Entries carry the Workshop id as well as the name: the mod info dialog links each one, and a
    // name alone is not something you can navigate to.
    const dependents = new Map<string, RelatedMod[]>();
    const requirements = new Map<string, RelatedMod[]>();
    if (!mods.value) {
      return { dependents, requirements, nameFor: displayReference, providerOf: () => undefined };
    }
    // A mod that cannot load on this build satisfies nothing, so nothing depends on it in any sense
    // that matters. Claiming otherwise would argue against removing the one mod we are telling them
    // to remove — the same trap that had it suppressing the dependency errors it caused.
    //
    // ⚠️ `buildMajor` from the backend, never re-derived from `build`. `build` is null until the game
    // has written its version file, and the fallback is `repoKey`, which only the backend sees.
    const major = mods.value.buildMajor;
    const loadable = (item: { supportsB41?: boolean | null; supportsB42?: boolean | null }) =>
      (major >= 42 ? item.supportsB42 : item.supportsB41) !== false;

    // 🚨 The live selection, not the one the backend saw. `mods` is computed on the last config
    // read, so a mod deleted in Manage Modlist is still in it until the customer saves — and it went
    // on being counted as a dependent, so removing the library it needed still asked "are you sure,
    // X requires this?" about an X that was no longer there. Everything downstream of this index
    // reads better for it too: a tag or a note about a mod you have just deleted is noise.
    const selected = new Set(base.subscribedFileIds.value);
    const inList = (workshopId: string | null | undefined) => !!workshopId && selected.has(workshopId);

    const provider = new Map<string, string>();
    const names = new Map<string, string>();
    const declares = new Map<string, Set<string>>();

    function note(workshopId: string | null | undefined, modId: string, references: string[], name?: string) {
      if (!workshopId) {
        return;
      }
      provider.set(modId.toLowerCase(), workshopId);
      provider.set(`${workshopId}/${modId.toLowerCase()}`, workshopId);
      if (name) {
        names.set(workshopId, name);
      }
      const bucket = declares.get(workshopId) ?? new Set<string>();
      references.forEach((reference) => bucket.add(normalizeReference(reference)));
      declares.set(workshopId, bucket);
    }

    for (const item of mods.value.selected) {
      if (!inList(item.workshopId)) {
        continue;
      }
      names.set(item.workshopId, item.name || item.workshopId);
      if (loadable(item)) {
        item.modIds.forEach((modId) => note(item.workshopId, modId, item.requires, item.name ?? undefined));
      }
      // An item with no mod ids yet still declares what it needs — the catalog carries that. Without
      // it, removing a library right after adding something that needs it goes through silently,
      // which is exactly when it is easiest to do by accident.
      const bucket = declares.get(item.workshopId) ?? new Set<string>();
      item.requires.forEach((reference) => bucket.add(normalizeReference(reference)));
      declares.set(item.workshopId, bucket);
    }
    for (const mod of mods.value.installed) {
      if (inList(mod.workshopId) && loadable(mod)) {
        note(mod.workshopId, mod.modId, mod.requires, mod.name);
      }
    }
    // Items added since the last config read. The backend model has never seen them — it is computed
    // on a read — but `addMod` keeps the whole catalog row, so what they provide and what they need
    // is known the moment they are picked. Without this a mod added to satisfy a dependency does not
    // count as satisfying it until the customer saves.
    for (const mod of justAdded.value) {
      const workshopId = mod.id?.toString();
      const references = mod.zomboid?.requires ?? [];
      (mod.zomboid?.modIds ?? []).forEach((modId) => note(workshopId, modId, references, mod.name));
      if (workshopId) {
        names.set(workshopId, mod.name || workshopId);
        const bucket = declares.get(workshopId) ?? new Set<string>();
        references.forEach((reference) => bucket.add(normalizeReference(reference)));
        declares.set(workshopId, bucket);
      }
    }

    /**
     * A `require=` reference as something a human recognises.
     *
     * Resolved against the customer's own list, which costs nothing. Anything else stays as the mod
     * author wrote it: `find_zomboid_mod_providers` could name it, but that is one indexed query per
     * id — fine for the handful a broken list produces, far too much for a search page that reruns
     * on every keystroke.
     */
    const nameFor = (reference: string) => {
      const target = provider.get(normalizeReference(reference));
      return (target && names.get(target)) || displayReference(reference);
    };

    /** The selected Workshop item that satisfies a reference, if any of them does. */
    const providerOf = (reference: string) => provider.get(normalizeReference(reference));

    for (const [workshopId, references] of declares) {
      for (const reference of references) {
        const target = provider.get(reference);
        // 🚨 Self-exclusion is not tidiness: Ammocraft declares `require=\2245813444/ammocraft`,
        // its own id. Counting that would make it permanently depend on, and be depended on by,
        // itself.
        if (!target || target === workshopId) {
          continue;
        }
        const dependent = { workshopId, name: names.get(workshopId) ?? workshopId };
        const requirement = { workshopId: target, name: names.get(target) ?? target };
        dependents.set(target, [...(dependents.get(target) ?? []), dependent]);
        requirements.set(workshopId, [...(requirements.get(workshopId) ?? []), requirement]);
      }
    }
    return { dependents, requirements, nameFor, providerOf };
  });

  /**
   * Drop one mod id from `Mods=`.
   *
   * The only sanctioned way to edit that line by hand, and it exists for exactly one situation: an
   * id the derivation cannot attribute to any Workshop item. Those are never removed automatically —
   * the id could belong to a mod hand-uploaded to `mods/`, or to one whose scan failed, and deleting
   * either would be worse than leaving it. A save import can hand a customer such an id with no
   * Workshop item behind it at all (`Mods=Greenfire`, `WorkshopItems=`), and without this there is
   * nothing they can do about it from the panel.
   *
   * Marks the config dirty rather than committing: this is a change to the mod list like any other,
   * so the customer sees it in the save bar before it lands.
   */
  function removeModId(modId: string) {
    activeModIds.value = activeModIds.value.filter((id) => id.toLowerCase() !== modId.toLowerCase());
  }

  /** Whether a mod id is still switched on — lets the panel hide a finding it has just fixed. */
  function isModIdActive(modId?: string | null) {
    return !!modId && activeModIds.value.some((id) => id.toLowerCase() === modId.toLowerCase());
  }

  /** Selected mods that require anything this Workshop item provides. */
  function dependentsOf(workshopId?: string) {
    return workshopId ? dependencyIndex.value.dependents.get(workshopId) ?? [] : [];
  }

  /** Selected mods this Workshop item requires. */
  function requirementsOf(workshopId?: string) {
    return workshopId ? dependencyIndex.value.requirements.get(workshopId) ?? [] : [];
  }

  /**
   * The two related-mod lists for the mod info dialog (B.9).
   *
   * The dialog is shared, so this hands it plain labelled links rather than anything Project
   * Zomboid-shaped. What is game-specific stays here: which references count, how they resolve to
   * Workshop items, and the fact that a dependency we cannot resolve is worth showing anyway.
   */
  function modRelations(mod: SteamWorkshopItem): ModRelation[] {
    const workshopId = mod.id?.toString();
    const item = (mods.value?.selected ?? []).find((entry) => entry.workshopId === workshopId);
    const { nameFor, providerOf } = dependencyIndex.value;
    const relations: ModRelation[] = [];

    // From disk once the server has the mod, from the catalog while browsing. Both are the same
    // `require=` lines; only the source differs.
    const references = [
      ...new Set(
        item
          ? externalRequires(item.requires, item.modIds, workshopId)
          : externalRequires(mod.zomboid?.requires, mod.zomboid?.modIds, workshopId),
      ),
    ];
    if (references.length) {
      relations.push({
        key: "dependencies",
        title: t("ngp/zomboid_mods/dialog/dependencies"),
        entries: references.map((reference) => {
          const target = providerOf(reference);
          return {
            label: nameFor(reference),
            url: target ? `${STEAM_ITEM_URL}${target}` : undefined,
            // The one thing worth flagging: this mod needs something the list does not have. It is
            // also reported in the notices above, but this is where the customer is looking when
            // they are deciding whether to add the mod at all.
            note: target ? undefined : t("ngp/zomboid_mods/dialog/not_in_list"),
          };
        }),
      });
    }

    // ⚠️ Only meaningful for a mod that is in the list. For a search result we do not know which mod
    // ids it would contribute — `buildMetadata` is deliberately not fetched on the browse path — so
    // an empty list here would mean "we did not look", not "nothing needs it".
    const dependents = dependentsOf(workshopId);
    if (item && dependents.length) {
      relations.push({
        key: "dependents",
        title: t("ngp/zomboid_mods/dialog/dependents"),
        entries: dependents.map((entry) => ({
          label: entry.name,
          url: `${STEAM_ITEM_URL}${entry.workshopId}`,
        })),
      });
    }
    return relations;
  }

  /** The selected item's provenance, or undefined for one we never recorded a reason for (B.8a). */
  function originOf(workshopId?: string) {
    const item = (mods.value?.selected ?? []).find((entry) => entry.workshopId === workshopId);
    if (!item?.addedAs) {
      return undefined;
    }
    return {
      addedAs: item.addedAs,
      addedFor: item.addedFor.map((id) => selectedName(id)),
    };
  }

  function selectedName(workshopId: string) {
    return (mods.value?.selected ?? []).find((entry) => entry.workshopId === workshopId)?.name || workshopId;
  }

  return {
    ...base,
    modTags,
    modHint,
    modNotes,
    modRelations,
    removeMod,
    dependentsOf,
    requirementsOf,
    originOf,
    pendingIssuesInput,
    removeModId,
    isModIdActive,
  };
}

/**
 * Same normalisation the backend uses: `require=` entries carry a leading `\` and may be written as
 * `<workshopId>/<modId>`, so comparing them verbatim matches nothing.
 */
function normalizeReference(reference: string) {
  return reference.replace(/^\\+/, "").trim().toLowerCase();
}

/**
 * A Workshop item's `require=` entries, minus the ones it satisfies itself.
 *
 * 🚨 One Workshop item ships several mods, and they routinely declare each other — More Traits
 * (1299328280) requires `ToadTraits`, which More Traits itself provides. Counting those made the
 * card advertise *"Requires 3 mods"* while only two notices could ever appear, since the checks
 * correctly treat an item's own ids as satisfied. A tag that disagrees with the notice under it
 * reads as a missing notification.
 *
 * Both id spellings are considered: B42 writes `<workshopId>/<modId>` and a reference may use
 * either form.
 */
export function externalRequires(references?: string[] | null, providedModIds?: string[] | null, workshopId?: string) {
  const own = new Set<string>();
  for (const modId of providedModIds ?? []) {
    const key = modId.toLowerCase();
    const bare = key.slice(key.indexOf("/") + 1);
    own.add(key).add(bare);
    if (workshopId) {
      own.add(`${workshopId}/${bare}`);
    }
  }
  return (references ?? []).filter((reference) => {
    const key = normalizeReference(reference);
    return !own.has(key) && !own.has(key.slice(key.indexOf("/") + 1));
  });
}

/** `[{name}, …]` as one readable list. */
function joinNames(entries: RelatedMod[]) {
  return entries.map((entry) => entry.name).join(", ");
}

/**
 * A `require=` entry as the author wrote it, minus the `\` — for showing, not for matching.
 *
 * Mirrors the backend's `display_reference`. Using the normalised form in a message hands the
 * customer `chuckleberryfinnalertsystem`, which is not what the mod is called anywhere.
 */
function displayReference(reference: string) {
  const text = reference.replace(/^\\+/, "").trim();
  const [head, ...rest] = text.split("/");
  return rest.length && /^\d+$/.test(head) ? rest.join("/") : text;
}

export default (_ctx: ProfileCtx): Partial<GameProfile<SteamWorkshopItem, IUseMods<SteamWorkshopItem>>> => ({
  configSections,
  menu: createMenu(),
  useMods,
  // The generic "not updated in 60 days, possibly incompatible" hint is actively wrong here: it
  // flagged Tomb's Player Body Overhaul and half the list purely on age. Project Zomboid mods
  // declare `versionMin`/`versionMax` and ship per-build folders, so the diagnostics panel gives an
  // exact per-mod verdict instead — and a wrong warning next to a right one devalues both.
  staleModWarning: false,
  usesRcon: true,
  statusPageConfig: {
    showRconPort: true,
    showQueryPort: false,
    showFtp: true,
  },
});
